import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArchiveBoxXMarkIcon,
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  InformationCircleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  EXIF_TRACKED_FIELDS,
  type ExifTrackedField,
  type OriginalsScanResult,
} from '@xphoto/shared';
import { useI18n } from '../i18n';
import { interpolate } from '../gallery/format';
import { formatBytes } from '../viewer/format';
import { useFolders } from '../gallery/use-facets';
import type { ExifDeepLink } from '../gallery/state';
import { ApiError } from '../gallery/api';
import { recordAction } from '../diagnostics/buffers';
import { Portal } from '../components/Portal';
import { cleanupOriginals, enqueueExifErase, scanOriginals } from './exif-api';
import { useExifAnalysis } from './use-exif-analysis';
import { FolderScope, InlineWarning, Toggle } from './exif-controls';
import { ExifEraseConfirmModal } from './ExifEraseConfirmModal';
import { OriginalsCleanupConfirmModal } from './OriginalsCleanupConfirmModal';

/**
 * Écran « NETTOYAGE EXIF » (T4b, XPHOTO-24/-04 ; refonte SPLIT v0.53.0).
 *
 * DEPUIS LE SPLIT, cette page ne fait QUE de l'EFFACEMENT (la PUBLICATION est passée
 * dans un outil séparé « Publier des tags vers EXIF »). C'est le centre du nettoyage :
 *
 * - des cartes d'ANALYSE de présence par champ EXIF, regroupées par thème. Chaque
 *   carte porte DEUX actions :
 *     · « Voir » → lien profond galerie filtrée sur ce champ (`hasExifField`) pour
 *       parcourir les photos qui le portent ;
 *     · « Effacer ce champ » → enfile un effacement CIBLÉ (`fields:[champ]`) sur toute
 *       la bibliothèque (ou le dossier de portée), derrière une modale de confirmation
 *       LOURDE (irréversible). Un champ absent partout (présence 0) désactive les deux.
 * - une section AVANCÉE : effacement GLOBAL de TOUTES les métadonnées embarquées
 *   (GPS compris). Les mêmes options partagées ci-dessus (simulation, `_original`,
 *   portée dossier) s'y appliquent.
 *
 * Options d'effacement PARTAGÉES (portée dossier, `_original`, dry-run) posées en tête,
 * appliquées à l'effacement ciblé comme à l'effacement global.
 *
 * ÉCRITURE OPT-IN : un 409 `WRITE_DISABLED` (bibliothèque en lecture seule) est
 * intercepté et affiché avec un message dédié (comme le dédoublonnage).
 *
 * PAGE rendue dans la zone `main` sous l'en-tête persistant (`AppChrome`) ; le fil
 * d'Ariane pilote le retour ; défile via son propre conteneur `overflow-y-auto`.
 */
interface ExifToolModalProps {
  libraryId: string;
  onClose: () => void;
  /** Lien profond depuis une carte → galerie filtrée (XPHOTO-24). */
  onDeepLink: (link: ExifDeepLink) => void;
}

/** Regroupement thématique des champs suivis (présentation seule). */
const FIELD_GROUPS: {
  titleKey: 'exif.groupCapture' | 'exif.groupCamera' | 'exif.groupRights' | 'exif.groupKeywords';
  fields: readonly ExifTrackedField[];
}[] = [
  // `Orientation` retiré : depuis l'outil « Pivoter », le tag Orientation n'est JAMAIS
  // effacé (l'effacer perdrait les rotations) → l'offrir ici serait un no-op trompeur.
  { titleKey: 'exif.groupCapture', fields: ['DateTimeOriginal', 'CreateDate', 'GPSLatitude'] },
  { titleKey: 'exif.groupCamera', fields: ['Make', 'Model', 'LensModel', 'Software'] },
  { titleKey: 'exif.groupRights', fields: ['Artist', 'Copyright'] },
  { titleKey: 'exif.groupKeywords', fields: ['Keywords', 'Subject'] },
];

/** Options d'effacement partagées (ciblé + global). */
interface EraseOptions {
  folder: string;
  keepOriginal: boolean;
  dryRun: boolean;
}

/** État d'un effacement en cours (feedback global de la page). */
type EraseFeedback =
  { kind: 'queued'; jobId: string } | { kind: 'error' } | { kind: 'writeDisabled' } | null;

export function ExifToolModal({
  libraryId,
  onClose,
  onDeepLink,
}: ExifToolModalProps): React.JSX.Element {
  const { t } = useI18n();
  const [refreshKey, setRefreshKey] = useState(0);

  // Options d'effacement partagées par les deux modes. « Conserver un original » est
  // DÉCOCHÉ par défaut (retour utilisateur : le défaut coché créait des centaines de
  // `_original` inaperçus). L'utilisateur peut l'activer explicitement (opt-in).
  const [options, setOptions] = useState<EraseOptions>({
    folder: '',
    keepOriginal: false,
    dryRun: false,
  });

  // L'analyse est RE-SCOPÉE sur la portée dossier : un changement recharge les
  // cartes (compteurs par champ) sur ce seul sous-arbre (`undefined` = toute la biblio).
  const analysis = useExifAnalysis(libraryId, refreshKey, options.folder || undefined);
  const folders = useFolders(libraryId, refreshKey);

  // Feedback + verrou réseau partagés (une seule action d'effacement à la fois).
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<EraseFeedback>(null);

  // Modale de confirmation : décrit la cible (champ ciblé, ou effacement global).
  const [pending, setPending] = useState<{ fields?: ExifTrackedField[] } | null>(null);

  // Champs EFFACÉS pendant cette session (hors dry-run) → une carte retombée à 0 qui
  // en fait partie affiche « ✓ nettoyé » (vs simplement grisée = « jamais présent »).
  const [cleanedFields, setCleanedFields] = useState<ReadonlySet<ExifTrackedField>>(new Set());

  const folderOptions = useMemo(() => (folders.data ?? []).map((f) => f.path), [folders.data]);

  const handleJobQueued = useCallback(() => setRefreshKey((k) => k + 1), []);

  // Envoi de l'effacement (ciblé si `fields`, sinon global avec `keep`).
  const runErase = useCallback(async (): Promise<void> => {
    if (pending === null || busy) return;
    setBusy(true);
    setFeedback(null);
    try {
      const job = await enqueueExifErase(libraryId, {
        // L'UI n'expose plus de « groupes à conserver » : le mode GLOBAL efface TOUT
        // (GPS compris). Le contrat garde `keep` ; on envoie simplement une liste vide.
        keep: [],
        ...(pending.fields ? { fields: pending.fields } : {}),
        ...(options.folder ? { folder: options.folder } : {}),
        keepOriginal: options.keepOriginal,
        dryRun: options.dryRun,
      });
      recordAction('exif.erase', { dryRun: options.dryRun ? 1 : 0 });
      // Une SIMULATION ne modifie rien → ne marque aucun champ « nettoyé ». Sinon on
      // mémorise les champs ciblés (ou tous, en mode global) : le badge « ✓ nettoyé »
      // n'apparaît que lorsque l'analyse rechargée confirmera la présence à 0.
      if (!options.dryRun) {
        const erased = pending.fields ?? EXIF_TRACKED_FIELDS;
        setCleanedFields((prev) => {
          const next = new Set(prev);
          for (const f of erased) next.add(f);
          return next;
        });
      }
      setFeedback({ kind: 'queued', jobId: job.id });
      setPending(null);
      handleJobQueued();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.code === 'WRITE_DISABLED') {
        setFeedback({ kind: 'writeDisabled' });
      } else {
        setFeedback({ kind: 'error' });
      }
      setPending(null);
    } finally {
      setBusy(false);
    }
  }, [pending, busy, libraryId, options, handleJobQueued]);

  // Résumé de la portée (pour le corps de la modale de confirmation).
  const scopeLabel = options.folder
    ? interpolate(t('exif.eraseScopeFolder'), { folder: options.folder })
    : t('exif.eraseScopeAll');

  const confirmBody = pending
    ? pending.fields
      ? interpolate(t('exif.eraseConfirmFieldBody'), {
          field: pending.fields[0] ?? '',
          scope: scopeLabel,
        })
      : interpolate(t('exif.eraseConfirmAllBody'), { scope: scopeLabel })
    : '';

  const confirmTitle = pending
    ? pending.fields
      ? t('exif.eraseConfirmFieldTitle')
      : t('exif.eraseConfirmAllTitle')
    : '';

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* En-tête de page. */}
      <div className="flex items-center justify-between gap-2 border-b border-border pb-4">
        <h1 className="flex items-center gap-2 text-lg font-semibold text-fg">
          <TrashIcon className="h-6 w-6" aria-hidden="true" />
          {t('exif.title')}
        </h1>
        <button
          type="button"
          onClick={onClose}
          title={t('exif.close')}
          aria-label={t('exif.close')}
          className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          <ArrowUturnLeftIcon className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>

      {/* Corps défilant (le seul conteneur de défilement de la page). `relative` : ancre
          les descendants `sr-only`/`absolute` à ce conteneur, pas au document (cf. v0.48.0). */}
      <div className="relative min-h-0 flex-1 overflow-y-auto py-4">
        <div className="mx-auto flex max-w-4xl flex-col gap-8">
          <p className="text-sm text-muted">{t('exif.intro')}</p>

          {analysis.status === 'error' ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <ExclamationTriangleIcon className="h-10 w-10 text-danger" aria-hidden="true" />
              <p className="text-fg">{t('exif.errorTitle')}</p>
              <button
                type="button"
                onClick={analysis.reload}
                className="rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              >
                {t('exif.retry')}
              </button>
            </div>
          ) : analysis.status === 'ready' && analysis.data ? (
            <>
              {/* Options d'effacement partagées (portée, _original, dry-run). */}
              <EraseOptionsBar
                options={options}
                folderOptions={folderOptions}
                onChange={setOptions}
              />

              {/* Note : l'effacement resynchronise l'index tout seul (pas de rescan). */}
              <RefreshNote onRefresh={analysis.reload} />

              {/* Feedback global (une action à la fois). */}
              {feedback && <EraseFeedbackLine feedback={feedback} />}

              <AnalysisSection
                total={analysis.data.total}
                fields={analysis.data.fields}
                cleanedFields={cleanedFields}
                busy={busy}
                onSelectField={(field) =>
                  onDeepLink({
                    kind: 'field',
                    field,
                    ...(options.folder ? { folder: options.folder } : {}),
                  })
                }
                onEraseField={(field) => setPending({ fields: [field] })}
              />

              {/* Sûreté : trouver/supprimer les sauvegardes `_original` d'ExifTool. */}
              <OriginalsCleanupPanel libraryId={libraryId} folder={options.folder} />

              <AdvancedErasePanel busy={busy} onEraseAll={() => setPending({})} />
            </>
          ) : (
            <p className="py-12 text-center text-muted" role="status">
              {t('exif.loading')}
            </p>
          )}
        </div>
      </div>

      {/* Modale de confirmation LOURDE (ciblé ou global). */}
      {pending !== null && (
        <Portal>
          <ExifEraseConfirmModal
            title={confirmTitle}
            body={confirmBody}
            busy={busy}
            onConfirm={() => void runErase()}
            onClose={() => {
              if (!busy) setPending(null);
            }}
          />
        </Portal>
      )}
    </div>
  );
}

/** Barre d'options d'effacement partagées (portée dossier, `_original`, dry-run). */
function EraseOptionsBar({
  options,
  folderOptions,
  onChange,
}: {
  options: EraseOptions;
  folderOptions: readonly string[];
  onChange: (next: EraseOptions) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  return (
    <section
      aria-labelledby="exif-options-heading"
      className="flex flex-col gap-3 rounded-lg border border-border p-4"
    >
      <h2 id="exif-options-heading" className="text-sm font-semibold text-fg">
        {t('exif.eraseOptionsTitle')}
      </h2>
      <FolderScope
        idPrefix="erase"
        value={options.folder}
        options={folderOptions}
        onChange={(folder) => onChange({ ...options, folder })}
      />
      <div className="flex flex-col gap-2">
        <Toggle
          checked={options.keepOriginal}
          onChange={(keepOriginal) => onChange({ ...options, keepOriginal })}
          label={t('exif.keepOriginal')}
          hint={t('exif.keepOriginalHint')}
        />
        {/* Décoché : écriture SANS sauvegarde (irréversible) — avertissement explicite. */}
        {!options.keepOriginal && <InlineWarning text={t('exif.noBackupWarning')} />}
        <Toggle
          checked={options.dryRun}
          onChange={(dryRun) => onChange({ ...options, dryRun })}
          label={t('exif.dryRun')}
          hint={t('exif.dryRunHint')}
        />
      </div>
    </section>
  );
}

/** Ligne de feedback global d'un effacement (icône + texte, jamais la couleur seule). */
function EraseFeedbackLine({
  feedback,
}: {
  feedback: NonNullable<EraseFeedback>;
}): React.JSX.Element {
  const { t } = useI18n();
  if (feedback.kind === 'queued') {
    return (
      <p role="status" className="inline-flex items-center gap-1 text-sm text-fg">
        <CheckCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
        {interpolate(t('exif.jobQueued'), { id: feedback.jobId.slice(0, 8) })}
      </p>
    );
  }
  return (
    <p role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
      <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
      {feedback.kind === 'writeDisabled' ? t('exif.errorWriteDisabled') : t('exif.jobFailed')}
    </p>
  );
}

/**
 * Cartes d'analyse : présence par champ suivi, regroupées par thème. Chaque carte
 * porte « Voir » (lien profond galerie, XPHOTO-24) + « Effacer ce champ » (effacement
 * ciblé en batch). Un champ à présence 0 désactive les deux actions.
 */
function AnalysisSection({
  total,
  fields,
  cleanedFields,
  busy,
  onSelectField,
  onEraseField,
}: {
  total: number;
  fields: { key: ExifTrackedField; present: number }[];
  cleanedFields: ReadonlySet<ExifTrackedField>;
  busy: boolean;
  onSelectField: (field: ExifTrackedField) => void;
  onEraseField: (field: ExifTrackedField) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  const byKey = useMemo(() => {
    const map = new Map<ExifTrackedField, number>();
    for (const f of fields) map.set(f.key, f.present);
    // Garantit une entrée par champ suivi même si l'API en omet.
    for (const key of EXIF_TRACKED_FIELDS) if (!map.has(key)) map.set(key, 0);
    return map;
  }, [fields]);

  return (
    <section aria-labelledby="exif-analysis-heading" className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h3 id="exif-analysis-heading" className="text-base font-semibold text-fg">
          {t('exif.analysisTitle')}
        </h3>
        <p className="text-sm text-muted">{interpolate(t('exif.analysisTotal'), { total })}</p>
        <p className="text-xs text-muted">{t('exif.analysisHint')}</p>
      </div>

      {FIELD_GROUPS.map((group) => (
        <div key={group.titleKey} className="flex flex-col gap-2">
          <h4 className="text-sm font-medium text-muted">{t(group.titleKey)}</h4>
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {group.fields.map((key) => {
              const present = byKey.get(key) ?? 0;
              // Champ absent partout : ni « Voir » (grille vide) ni « Effacer » (rien à faire).
              const disabled = present === 0;
              // Retombé à 0 APRÈS un effacement de cette session → « ✓ nettoyé ».
              const cleaned = present === 0 && cleanedFields.has(key);
              return (
                <li
                  key={key}
                  className="flex flex-col gap-2 rounded-lg border border-border bg-surface-2 p-3"
                >
                  <div className="flex flex-col gap-1">
                    <span className="break-words font-mono text-sm text-fg">{key}</span>
                    <span className="flex items-center gap-1.5 text-sm text-muted">
                      {interpolate(t('exif.fieldPresence'), { present, total })}
                      {cleaned && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success">
                          <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                          {t('exif.cleanedBadge')}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => onSelectField(key)}
                      title={interpolate(t('exif.filterByField'), { field: key })}
                      aria-label={interpolate(t('exif.filterByField'), { field: key })}
                      className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-fg enabled:hover:border-brand enabled:hover:bg-surface disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                    >
                      <EyeIcon className="h-4 w-4" aria-hidden="true" />
                      {t('exif.viewField')}
                    </button>
                    <button
                      type="button"
                      disabled={disabled || busy}
                      onClick={() => onEraseField(key)}
                      title={interpolate(t('exif.eraseFieldAction'), { field: key })}
                      aria-label={interpolate(t('exif.eraseFieldAction'), { field: key })}
                      className="inline-flex items-center gap-1 rounded-md border border-danger/50 bg-danger/10 px-2 py-1 text-xs font-medium text-danger enabled:hover:bg-danger/20 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger"
                    >
                      <TrashIcon className="h-4 w-4" aria-hidden="true" />
                      {t('exif.eraseField')}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </section>
  );
}

/**
 * Note explicative (Correctif 3) : après un effacement en lot, l'index est
 * resynchronisé automatiquement pour les photos concernées — PAS besoin de rescanner
 * toute la bibliothèque. L'analyse se rafraîchit seule en fin de job ; le bouton
 * « Actualiser l'analyse » force un rechargement immédiat (icône + texte).
 */
function RefreshNote({ onRefresh }: { onRefresh: () => void }): React.JSX.Element {
  const { t } = useI18n();
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-surface-2 px-3 py-2">
      <p className="inline-flex items-start gap-2 text-sm text-muted">
        <InformationCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
        <span>{t('exif.autoResyncNote')}</span>
      </p>
      <button
        type="button"
        onClick={onRefresh}
        className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-fg hover:border-brand hover:bg-surface focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
        {t('exif.refreshAnalysis')}
      </button>
    </div>
  );
}

/** État de l'analyse des sauvegardes `_original`. */
type OriginalsState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; result: OriginalsScanResult }
  | { kind: 'error' };

/** Feedback de la suppression des sauvegardes `_original`. */
type OriginalsFeedback =
  { kind: 'queued'; jobId: string } | { kind: 'error' } | { kind: 'writeDisabled' } | null;

/**
 * Panneau de SÛRETÉ « Fichiers de sauvegarde `_original` » (fonctionnalité 4).
 *
 * Répond au « je ne sais pas où ni combien » : bouton « Rechercher » (parcours FS
 * LECTURE, portée dossier héritée des options) → affiche N trouvés + taille totale +
 * un échantillon de chemins ; puis « Supprimer les sauvegardes » derrière une modale
 * de confirmation LOURDE (enfile un job — progression dans la cloche). 409
 * `WRITE_DISABLED` (lecture seule) intercepté avec message dédié.
 */
function OriginalsCleanupPanel({
  libraryId,
  folder,
}: {
  libraryId: string;
  folder: string;
}): React.JSX.Element {
  const { t, locale } = useI18n();
  const [state, setState] = useState<OriginalsState>({ kind: 'idle' });
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [feedback, setFeedback] = useState<OriginalsFeedback>(null);

  // Une nouvelle portée dossier invalide le résultat précédent (il ne correspond plus).
  useEffect(() => {
    setState({ kind: 'idle' });
    setFeedback(null);
  }, [folder]);

  const scopeLabel = folder
    ? interpolate(t('exif.eraseScopeFolder'), { folder })
    : t('exif.eraseScopeAll');

  const runScan = useCallback(async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setFeedback(null);
    setState({ kind: 'loading' });
    try {
      const result = await scanOriginals(libraryId, folder || undefined);
      setState({ kind: 'ready', result });
    } catch {
      setState({ kind: 'error' });
    } finally {
      setBusy(false);
    }
  }, [busy, libraryId, folder]);

  const runCleanup = useCallback(async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setFeedback(null);
    try {
      const job = await cleanupOriginals(libraryId, { ...(folder ? { folder } : {}) });
      recordAction('exif.originals_cleanup', {});
      setFeedback({ kind: 'queued', jobId: job.id });
      setConfirming(false);
      // Le résultat affiché n'est plus fiable une fois la suppression enfilée.
      setState({ kind: 'idle' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.code === 'WRITE_DISABLED') {
        setFeedback({ kind: 'writeDisabled' });
      } else {
        setFeedback({ kind: 'error' });
      }
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }, [busy, libraryId, folder]);

  const found = state.kind === 'ready' ? state.result.total : 0;
  const confirmBody = interpolate(
    t('exif.originals.confirmBody'),
    {
      count: found,
      size: state.kind === 'ready' ? formatBytes(state.result.totalBytes, locale) : '',
      scope: scopeLabel,
    },
    locale,
  );

  return (
    <section
      aria-labelledby="exif-originals-heading"
      className="flex flex-col gap-4 rounded-lg border border-border p-4"
    >
      <div className="flex flex-col gap-1">
        <h3
          id="exif-originals-heading"
          className="flex items-center gap-2 text-base font-semibold text-fg"
        >
          <ArchiveBoxXMarkIcon className="h-5 w-5" aria-hidden="true" />
          {t('exif.originals.title')}
        </h3>
        <p className="text-sm text-muted">{t('exif.originals.hint')}</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void runScan()}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-medium text-fg hover:border-brand hover:bg-surface disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          <ArrowPathIcon
            className={`h-5 w-5${state.kind === 'loading' ? ' animate-spin' : ''}`}
            aria-hidden="true"
          />
          {t('exif.originals.scan')}
        </button>

        {feedback?.kind === 'queued' && (
          <p role="status" className="inline-flex items-center gap-1 text-sm text-fg">
            <CheckCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
            {interpolate(t('exif.jobQueued'), { id: feedback.jobId.slice(0, 8) })}
          </p>
        )}
        {(feedback?.kind === 'error' || feedback?.kind === 'writeDisabled') && (
          <p role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
            {feedback.kind === 'writeDisabled' ? t('exif.errorWriteDisabled') : t('exif.jobFailed')}
          </p>
        )}
      </div>

      {state.kind === 'error' && (
        <p role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
          {t('exif.originals.error')}
        </p>
      )}

      {state.kind === 'ready' && (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-fg">
            {found === 0
              ? t('exif.originals.none')
              : interpolate(
                  t('exif.originals.found'),
                  {
                    count: found,
                    size: formatBytes(state.result.totalBytes, locale),
                  },
                  locale,
                )}
          </p>

          {found > 0 && state.result.sample.length > 0 && (
            <ul className="max-h-40 overflow-y-auto rounded-md border border-border bg-surface-2 p-2 text-xs text-muted">
              {state.result.sample.map((b) => (
                <li key={b.relPath} className="flex justify-between gap-3 py-0.5">
                  <span className="truncate font-mono">{b.relPath}</span>
                  <span className="shrink-0 tabular-nums">{formatBytes(b.sizeBytes, locale)}</span>
                </li>
              ))}
            </ul>
          )}

          {found > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setConfirming(true)}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-md bg-danger px-4 py-2 text-sm font-medium text-danger-fg disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger"
              >
                <ArchiveBoxXMarkIcon className="h-5 w-5" aria-hidden="true" />
                {t('exif.originals.delete')}
              </button>
            </div>
          )}
        </div>
      )}

      {confirming && (
        <Portal>
          <OriginalsCleanupConfirmModal
            body={confirmBody}
            busy={busy}
            onConfirm={() => void runCleanup()}
            onClose={() => {
              if (!busy) setConfirming(false);
            }}
          />
        </Portal>
      )}
    </section>
  );
}

/**
 * Section AVANCÉE : effacement GLOBAL (`-all=`) de TOUTES les métadonnées embarquées,
 * GPS compris. La conservation par champ est gérée par les cartes « Effacer ce champ »
 * ci-dessus → ici il n'y a plus de « groupes à conserver ». Ouvre la même modale de
 * confirmation (mode global). Les options partagées (simulation, `_original`, portée
 * dossier) s'y appliquent AUSSI — rappelé explicitement.
 */
function AdvancedErasePanel({
  busy,
  onEraseAll,
}: {
  busy: boolean;
  onEraseAll: () => void;
}): React.JSX.Element {
  const { t } = useI18n();

  return (
    <section
      aria-labelledby="exif-erase-heading"
      className="flex flex-col gap-4 rounded-lg border border-border p-4"
    >
      <div className="flex flex-col gap-1">
        <h3
          id="exif-erase-heading"
          className="flex items-center gap-2 text-base font-semibold text-fg"
        >
          <TrashIcon className="h-5 w-5" aria-hidden="true" />
          {t('exif.eraseTitle')}
        </h3>
        <p className="text-sm text-muted">{t('exif.eraseHint')}</p>
      </div>

      {/* Rappel : les options partagées gouvernent AUSSI « Effacer tout » (icône + texte,
          jamais la couleur seule) — lève la confusion « Tout effacer échappe-t-il au dry-run ? ». */}
      <p className="inline-flex items-start gap-2 rounded-md bg-surface-2 px-3 py-2 text-sm text-muted">
        <InformationCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
        <span>{t('exif.eraseAllOptionsNote')}</span>
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={busy}
          onClick={onEraseAll}
          className="inline-flex items-center gap-2 rounded-md bg-danger px-4 py-2 text-sm font-medium text-danger-fg disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger"
        >
          <TrashIcon className="h-5 w-5" aria-hidden="true" />
          {t('exif.erase')}
        </button>
      </div>
    </section>
  );
}
